import Bluebird from 'bluebird';

import moment from 'moment';
import React from 'react';
import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import {render, hydrate, unmountComponentAtNode} from 'react-dom';
import {match, Router, applyRouterMiddleware} from 'react-router';
import createBrowserHistory from 'history/lib/createBrowserHistory';
import createRouterHistory from 'react-router/lib/createRouterHistory';
// import DevTools from 'mobx-react-devtools';
import {observe, action, reaction} from 'mobx';
import getDeepForceUpdate from 'react-deep-force-update';

import Cookies from 'js-cookie';
import uuidv4 from 'uuid/v4';

// if (__DEV__) {
//   // mobx dev tools
//   const div = document.createElement('div');
//   $app.parentNode.insertBefore(div, $app);
//   hydrate(
//     <DevTools position={{bottom: 0, left: 20}} />,
//     div,
//   );
// }

import {AUTH_COOKIE_NAME} from './state/session';

import Context from './Components/Context';
import {serialize} from './store';

import setupTrustPilot from './vendor/trustpilot';
import setupAttribution from './vendor/attribution';

window.Promise = Bluebird;

const isServer = typeof window === 'undefined';
const {
  API_BASE, HTTP_HOST, NODE_ENV, RELEASE_VERSION, SENTRY_DSN, SENTRY_TRACE_RATE,
} = process.env;

// Remove the https:// prefix that exists on API_BASE - Maybe move to using Sentry specific domain lists? Also static.urban?
const BROWSERSAFE_API_BASE = isServer ? API_BASE : window.API_BASE;
const sanitisedApiBase = typeof BROWSERSAFE_API_BASE === 'string' ? BROWSERSAFE_API_BASE.replace('https://', '') : '';

Sentry.init({
  dsn: isServer ? SENTRY_DSN : window.SENTRY_DSN,
  integrations: [new TracingIntegrations.BrowserTracing({
    tracingOrigins: [
      'localhost',
      isServer ? HTTP_HOST : window.HTTP_HOST,
      sanitisedApiBase,
    ],
    shouldCreateSpanForRequest: (url) => {
      if (url.match(/\/health\/?/)) {
        return false;
      } else if (url.match(/\/pack\/\S*.[^\.js$]$/)) {
        // Adjust for things like pack/gwroihg.svg etc Not .js
        return true;
      }

      return true;
    },
  })],
  enabled: NODE_ENV !== 'development',
  environment: isServer ? NODE_ENV : window.ENV,
  release: isServer ? RELEASE_VERSION : window.RELEASE_VERSION,
  tracesSampleRate: parseFloat(isServer ? SENTRY_TRACE_RATE : window.SENTRY_TRACE_RATE) || 0,
});

const getCookie = Cookies.get;
const setCookie = (name, value, options) => {
  options = {path: '/', ...options};
  if (typeof options.maxAge === 'number') {
    options.expires = moment().add(options.maxAge, 'ms').toDate();
    delete options.maxAge;
  }

  Cookies.set(name, value, options);
};

const deepForceUpdate = getDeepForceUpdate(React);
const browserHistory = createRouterHistory(createBrowserHistory);

let currentUrl = browserHistory.getCurrentLocation().pathname;

let currentQuery = browserHistory.getCurrentLocation().search;

let currentQueryObject = browserHistory.getCurrentLocation().query;

if (__DEV__) {
  // expose useful public variables
  /* eslint-disable global-require */
  require('expose-loader?Cookies!js-cookie');
  require('expose-loader?React!react');
  require('expose-loader?ReactDOM!react-dom');
  require('expose-loader?QS!qs');
  require('expose-loader?Mobx!mobx');
  require('expose-loader?MobxReact!mobx-react');
  require('expose-loader?moment!moment-timezone');
  window.browserHistory = browserHistory;
  /* eslint-enable global-require */
  Bluebird.onPossiblyUnhandledRejection((error) => {
    throw error;
  });
  Bluebird.longStackTraces();
}

const $app = document.getElementById('app');

let createStore = require('./create-store').default;
const routes = require('./routes').default;

const history = browserHistory;

let store;
let location;
if (__DEV__ && module.hot && module.hot.data) {
  ({store} = module.hot.data);
} else {
  if (window.state) {
    // Decoding window.state, since it was encoded in the middleware to avoid XSS
    const newState = JSON.parse(decodeURIComponent(window.state));

    store = createStore({
      getCookie,
      setCookie,
      ...newState,
    });
  } else {
    store = createStore({
      getCookie,
      setCookie,
      session: {
        token: Cookies.get(AUTH_COOKIE_NAME),
      },
    });
  }

  location = window.locationState;
  if (location && window.history && window.history.state) {
    delete window.history.state.key;
  }
}

match({routes, history, location}, (error, redirectLocation, renderProps) => {
  let instance;

  let identifierCookie = Cookies.get('umDeviceIdentifier');

  // If there isn't a cookie with the uuid for umDeviceIdentifier, we then need to create one
  if (!identifierCookie) {
    identifierCookie = uuidv4();

    Cookies.set('umDeviceIdentifier', identifierCookie);
  }

  store.umDeviceIdentifier = identifierCookie;

  const utmCookie = Cookies.get('utm');

  if (utmCookie) {
    store.utmCookie = JSON.parse(utmCookie);
  }

  function renderApp() {
    instance = hydrate(
      <Context store={store} router={renderProps.router}>
        <Router {...renderProps} />
      </Context>,
      $app,
    );
  }

  let storeListener = observe(store.i18n, 'locale', action('rerender after locale change', () => {
    if (instance) {
      instance.forceUpdate();
    }
  }));

  if (__DEV__ && module.hot) {
    window.store = store;

    // Save / Restore the state of the store while this module is hot reloaded
    module.hot.accept('./store', () => {
      console.info('reloading store');
      storeListener();
      const state = serialize(store);
      createStore = require('./create-store').default; // eslint-disable-line global-require
      store = createStore({
        getCookie,
        setCookie,
        ...state,
      });
      window.store = store;
      setupTrustPilot();
      renderApp();
      storeListener = observe(store.i18n, 'locale', () => {
        deepForceUpdate(instance);
      });
      console.info('reloaded store');
    });

    // Rerender the entire app when routes change
    // module.hot.accept('./routes', () => {
    //   console.info('reloading routes');
    //   routes = require('./routes').default; // eslint-disable-line global-require
    //   unmountComponentAtNode(document.getElementById('app'));
    //   renderApp();
    //   console.info('reloaded routes');
    // });
  }

  /*
   *  Segment Analytics page tracking
   */
  history.listen((location) => {
    const newRoute = location.pathname;
    const newQuery = location.search;

    function preventScroll(queryProperty) {
      const existsCurrent = currentQuery.indexOf(queryProperty);
      const existsNew = newQuery.indexOf(queryProperty);

      return (
        (existsNew === -1 || existsCurrent === -1) &&
        existsCurrent !== existsNew &&
        newRoute === currentUrl
      );
    }

    function preventScrollSameScreen(queryProperty) {
      const currentQueryValue = currentQueryObject[queryProperty];
      const newQueryValue = location.query[queryProperty];

      return (
        currentQueryValue !== newQueryValue &&
        newRoute === currentUrl
      );
    }

    /*
     *  Scroll to the top of the page when we navigate between routes in the react app
     */
    if (
      window &&
      window.scrollY &&
      ((newRoute !== currentUrl) || (newQuery !== currentQuery))) {
      // If the change in the selection state is promocode, we don't want the page to scroll
      let stopScroll = preventScroll('promocode');

      // We don't want to scroll to top if the treatment sidepanel is opened
      if (!stopScroll) {
        stopScroll = preventScroll('treatmentInfoViewing');
      }

      // We don't want to scroll to top if the therapist sidepanel is opened
      if (!stopScroll) {
        stopScroll = preventScroll('therapistInfoViewing');
      }

      // We don't want to scroll to top if the changes are in the booking type and duration screen
      if (!stopScroll) {
        stopScroll =
          preventScrollSameScreen('duration') ||
          preventScrollSameScreen('duration2') ||
          preventScrollSameScreen('duration2Confirmed') ||
          preventScrollSameScreen('bookingType');
      }

      if (!stopScroll) {
        window.scroll(0, 0);
      }
    }

    if (newQuery !== currentQuery) {
      currentQuery = newQuery;
      currentQueryObject = location.query;
    }

    if (newRoute !== currentUrl) {
      const referrer = currentUrl;
      currentUrl = newRoute;
      // Due to the async nature of tracker.page, we need this timeout to be sure
      // that we're tracking the correct url
      setTimeout(() => store.tracker.page({referrer}), 200);
    }
  });

  setupTrustPilot();
  setupAttribution(store, renderProps);
  renderApp();
});



// WEBPACK FOOTER //
// ./spa/index.jsx